import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import GoogleIcon from "@mui/icons-material/Google";
import { signIn, signInWithRedirect } from "aws-amplify/auth";
import Footer from "../components/common/Footer";

export function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEmailSignIn = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn({
        username: email.trim().toLowerCase(),
        password,
        options: { authFlowType: "USER_SRP_AUTH" },
      });
      if (result.isSignedIn) {
        navigate("/", { replace: true });
      } else {
        setError("Additional steps are required to sign in. Please contact support.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sign-in failed.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background:
          "radial-gradient(circle at 20% 0%, #DCEFEE 0%, transparent 55%), radial-gradient(circle at 100% 100%, #FBE3D9 0%, transparent 50%), #F6F8F8",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Card
          elevation={0}
          sx={{
            maxWidth: 440,
            width: "100%",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(255,255,255,0.85)",
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <Stack spacing={3} alignItems="center">
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "linear-gradient(135deg, #0E7C7B 0%, #3FA2A1 100%)",
                  color: "#FFFFFF",
                  boxShadow: "0 8px 24px rgba(14, 124, 123, 0.25)",
                }}
              >
                <MailOutlineIcon sx={{ fontSize: 36 }} />
              </Box>
              <Stack spacing={0.5} alignItems="center">
                <Typography variant="h4" fontWeight={700} letterSpacing={-0.5}>
                  īmēra
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Sign in to manage transactional email across your apps.
                </Typography>
              </Stack>

              {error && (
                <Alert severity="error" sx={{ width: "100%" }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleEmailSignIn} sx={{ width: "100%" }}>
                <Stack spacing={2}>
                  <TextField
                    label="Email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                    disabled={submitting}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    required
                    disabled={submitting}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                  >
                    {submitting ? "Signing in…" : "Sign in"}
                  </Button>
                </Stack>
              </Box>

              <Divider sx={{ width: "100%" }}>
                <Typography variant="caption" color="text.secondary">
                  OR
                </Typography>
              </Divider>

              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={() => signInWithRedirect({ provider: "Google" })}
                disabled={submitting}
              >
                Continue with Google
              </Button>

              <Typography variant="caption" color="text.secondary" textAlign="center">
                Access is limited to allowlisted email addresses.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
      <Footer />
    </Box>
  );
}
